import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	map,
	of,
	switchMap,
	tap,
	withLatestFrom,
	filter,
} from 'rxjs';
import { NgCommService } from 'src/app/ajs-ng-communication.service';
import { AjsStateService } from 'src/app/ajs-state.service';
import { states } from 'src/app/core/application.states';
import { TimelineRequester } from 'src/app/requesters/timeline/timeline.requester';

import {
	ITimelineActionModalData,
	TimelineAction,
	TimelineActionPopupComponent,
} from '../components/timeline-action-popup/timeline-action-popup.component';
import { TimelineAdapter } from '../timeline.adapter';
import {
	bulkDeleteTimelines,
	deleteTimeline,
	deleteTimelineFail,
	deleteTimelineSuccess,
	duplicateTimeline,
	duplicateTimelineFail,
	duplicateTimelineSuccess,
	fetchTimelineListFail,
	fetchTimelineListSuccess,
	initTimelineList,
	navigateToTimeline,
	navigateToTimelineCreate,
	openDeleteConfirmationPopup,
	refreshTimelineList,
	toggleTimelineStatus,
	updateIncludePublic,
} from './timeline.actions';
import { TimelineSelector } from './timeline.selector';

@Injectable()
export class TimelineEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################
	public navigateToTimelineCreate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToTimelineCreate),
				tap(() => this.stateService.go(states.TimelineCreate))
			),
		{
			dispatch: false,
		}
	);

	public navigateToTimeline = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToTimeline),
				tap((a) =>
					a.goToVisuTab
						? this.stateService.go(states.TimelineVisualization, {
								id: `${a.id}`,
								mode: 'visualisation',
						  })
						: this.stateService.go(states.TimelineEdit, {
								id: `${a.id}`,
						  })
				)
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public initTimelineList$ = createEffect(() =>
		this.action$.pipe(
			ofType(initTimelineList),
			withLatestFrom(this.timelineSelector.getIncludePublic$()),
			switchMap(([, includePublic]) =>
				this.timelineRequester.getTimelineList(includePublic)
			),
			map((response) =>
				fetchTimelineListSuccess({
					timelineList: this.timelineAdapter.generateTimelineList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchTimelineListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshTimelineList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshTimelineList),
			withLatestFrom(this.timelineSelector.getIncludePublic$()),
			switchMap(([, includePublic]) =>
				this.timelineRequester.getTimelineList(includePublic, false)
			),
			map((response) =>
				fetchTimelineListSuccess({
					timelineList: this.timelineAdapter.generateTimelineList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchTimelineListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateIncludePublic$ = createEffect(() =>
		this.action$.pipe(
			ofType(updateIncludePublic),
			map(() => refreshTimelineList())
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleTimelineStatus),
			switchMap((action) =>
				this.timelineRequester.getTimeline(action.timelineId)
			),
			switchMap((timeline) =>
				this.timelineRequester.changeActivationStatusTimeline({
					...timeline,
					metadata: {
						...timeline.metadata,
						actif: !timeline.metadata.actif,
					},
				})
			),
			map(() => refreshTimelineList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshTimelineList());
				return caught;
			})
		)
	);

	public deleteTimeline$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteTimeline),
			switchMap((action) =>
				this.timelineRequester.deleteTimeline(action.timelineId)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@timeline.delete.success:`
				);
				this.store.dispatch(deleteTimelineSuccess());
				return refreshTimelineList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@timeline.delete.error:`
				);
				this.store.dispatch(
					deleteTimelineFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public bulkDeleteTimelines$ = createEffect(() =>
		this.action$.pipe(
			ofType(bulkDeleteTimelines),
			switchMap((action) =>
				this.timelineRequester.deleteManyTimelines(action.timelineIds)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@timeline.delete.multiple.success:`
				);
				this.store.dispatch(deleteTimelineSuccess());
				return refreshTimelineList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@timeline.delete.multiple.error:`
				);
				this.store.dispatch(
					deleteTimelineFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public duplicateTimeline$ = createEffect(() =>
		this.action$.pipe(
			ofType(duplicateTimeline),
			switchMap((action) =>
				this.timelineRequester.duplicateTimeline(action.timelineId)
			),
			map((id) => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@timeline.duplicate.success:`
				);
				return duplicateTimelineSuccess({
					id,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@timeline.duplicate.error:`
				);
				this.store.dispatch(duplicateTimelineFail(error));
				return caught;
			})
		)
	);

	public navigateToTimelineEditAfterDuplicate$ = createEffect(() =>
		this.action$.pipe(
			ofType(duplicateTimelineSuccess),
			map((action) =>
				navigateToTimeline({
					id: action.id,
					goToVisuTab: false,
				})
			)
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################
	public openDeleteConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openDeleteConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<ITimelineActionModalData>(
					TimelineActionPopupComponent,
					{
						title: $localize`:i18n=@@timeline.action.popup.delete.header:`,
						subTitle: '',
						timelineId: action.timelineId,
						element: action.timelineLabel,
						action: TimelineAction.Delete,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (action.timelineId.length === 1) {
					return deleteTimeline({
						timelineId: action.timelineId[0],
					});
				} else {
					return bulkDeleteTimelines({
						timelineIds: action.timelineId,
					});
				}
			})
		)
	);

	public constructor(
		private readonly action$: Actions,
		private readonly timelineRequester: TimelineRequester,
		private readonly timelineAdapter: TimelineAdapter,
		private readonly timelineSelector: TimelineSelector,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService
	) {}
}
